import {useCallback, useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowsRotate, faListCheck} from "@fortawesome/free-solid-svg-icons";
import ApiRoute from "../../../helper/api";
import {formatNumberWithDots, getDateAsReadableFormat} from "../../../helper/general";
import Modal from "../../Utilities/Modal";

const statusLabels = {
    running: 'در حال اجرا',
    pending: 'در انتظار',
    completed: 'تکمیل شده',
    failed: 'ناموفق',
    cancelled: 'لغو شده',
};

const modeLabels = {
    categories: 'دسته‌بندی‌ها',
    products: 'محصولات',
};

const count = (value) => formatNumberWithDots(Number(value ?? 0));

function RunMetric({label, value}) {
    return (
        <div className="bg-gray-1 rounded p-2 text-center">
            <div className="text-gray-5">{label}</div>
            <div className="text-gray-12 font-bold mt-1">{count(value)}</div>
        </div>
    );
}

function RunProgress({run}) {
    const total = Number(run.total_items ?? run.total ?? 0);
    const completed = Number(run.completed ?? 0);
    const progress = total > 0 ? Math.min(Math.round((completed / total) * 100), 100) : 0;

    return (
        <div className="mt-3">
            <div className="flex justify-between text-[11px] text-gray-5 mb-1">
                <span>پیشرفت</span>
                <span>{progress}٪</span>
            </div>
            <div className="h-2 bg-gray-7 rounded-full overflow-hidden" aria-label={`پیشرفت ${progress} درصد`}>
                <div className="h-full bg-green-2 rounded-full transition-all" style={{width: `${progress}%`}} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[11px]">
                <RunMetric label="کل" value={total}/>
                <RunMetric label="در انتظار" value={run.pending}/>
                <RunMetric label="در حال پردازش" value={run.processing}/>
                <RunMetric label="تکمیل‌شده" value={run.completed}/>
                <RunMetric label="تلاش مجدد" value={run.retry}/>
                <RunMetric label="ناموفق" value={run.failed}/>
                <RunMetric label="لغو شده" value={run.cancelled}/>
            </div>
        </div>
    );
}

function ActivePriceRuleRuns() {
    const [runs, setRuns] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRuns = useCallback(async (showLoading = false) => {
        if (showLoading) {
            setIsLoading(true);
        }

        try {
            const response = await ApiRoute.price.rule.activeRuns.fn();
            if (Array.isArray(response?.results)) {
                setRuns(response.results);
            }
        } catch (error) {
            console.error('Failed to load active price rule runs', error);
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        fetchRuns(true);
        const interval = setInterval(() => fetchRuns(), 5000);
        return () => clearInterval(interval);
    }, [fetchRuns, isOpen]);

    const openRuns = () => {
        setIsOpen(true);
    };

    return (
        <>
            <button
                type="button"
                onClick={openRuns}
                title="مشاهده ران‌های جاری"
                aria-label="مشاهده ران‌های جاری"
                className="relative text-purple-4 hover:text-purple-3 text-md lg:text-xl"
            >
                <FontAwesomeIcon icon={faListCheck}/>
                {runs.length > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full bg-red-1 text-white text-[10px] leading-4">
                        {count(runs.length)}
                    </span>
                )}
            </button>

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} modalWidth="w-full xl:w-2/3">
                <div className="page-title mb-4">
                    <span className="inline-block bg-white text-sm text-purple-1 pl-3">ران‌های جاری همگام‌سازی قیمت</span>
                </div>

                <div className="flex justify-end mb-4">
                    <button
                        type="button"
                        onClick={() => fetchRuns(true)}
                        className="btn-outline-gray text-xs"
                        disabled={isLoading}
                    >
                        <FontAwesomeIcon className="ml-2" icon={faArrowsRotate}/>
                        به‌روزرسانی
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="x2-loader" />
                    </div>
                ) : runs.length === 0 ? (
                    <div className="text-center text-sm text-gray-5 py-8">در حال حاضر ران جاری وجود ندارد.</div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {runs.map((run) => (
                            <div key={run.run_id} className="border border-gray-7 rounded-md p-3 text-xs">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="text-gray-12 font-bold">{run.rule_title || 'قانون بدون عنوان'}</div>
                                    <span className="green-tag !w-auto !inline-block !mx-0 !py-1 !px-3 text-center">
                                        {statusLabels[run.status] || run.status || '-'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-gray-5">
                                    <div>درخواست‌کننده: <span className="text-gray-12">{run.requested_by_name || '-'}</span></div>
                                    <div>نوع اجرا: <span className="text-gray-12">{modeLabels[run.mode] || run.mode || '-'}</span></div>
                                    <div>شروع: <span className="text-gray-12">{getDateAsReadableFormat(run.started_at, true) || '-'}</span></div>
                                </div>

                                <RunProgress run={run}/>

                                {run.last_error && (
                                    <div className="alert-red mt-3">خطای آخر: {run.last_error}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Modal>
        </>
    );
}

export default ActivePriceRuleRuns;
